import React from 'react'
import { makeStyles, Menu, Tooltip } from '@material-ui/core'

import { useHasAccess } from '../../utils/useHasAccess'
import { accessSchoolChat, accessMediations } from '../../utils/constants'

import privateRoomsSVG from '../../assets/icons/chat-private-bubble.svg'
import moderationRoomsSVG from '../../assets/icons/chat-moderation-bubble.svg'
import archivedRoomsSVG from '../../assets/icons/archive-icon.svg'
import exportSVG from '../../assets/icons/export-icon.svg'
import menuDotsSVG from '../../assets/icons/menu-dots.svg'

import featureFlags from '../../featureFlag/featureFlags'
import StyledSVG from '../shared/StyledSVG'
import TabNavigation from '../shared/TabNavigation'
import Button from '../button/Button'

import PrivateRoomMenuMessages from './PrivateRoomMenuMessages'
import GroupRoomMenuMessages from './GroupRoomMenuMessages'
import MediationRoomMenuMessages from './MediationRoomMenuMessages'

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%'
  },
  messageSideBarHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '& h1.label': {
      width: 'fit-content',
      fontWeight: 700,
      fontSize: 24,
      lineHeight: '100%',
      color: '#386093'
    },
    '& .btn_options_wrapper,.shortchut_options': {
      alignItems: 'center'
    },
    '& .btn_options_wrapper': {
      display: 'flex',
      justifyContent: 'space-between',
      width: 85
    },
    '& .without_menu_dots': {
      justifyContent: 'flex-end'
    },
    '& .shortcut_options': {
      display: 'flex',
      columnGap: 8,
      '& svg': {
        cursor: 'pointer'
      }
    },
    [theme.breakpoints.down('850')]: {
      flexDirection: 'column',
      '& .btn_options_wrapper': {
        width: 90
      }
    },
    [theme.breakpoints.down('768')]: {
      '& h1.label': {
        fontSize: 20
      }
    }
  },
  chatOptionsWrapper: {
    display: 'flex',
    overflowX: 'scroll',
    overflowY: 'hidden',
    minHeight: 'fit-content',
    columnGap: 8,
    paddingBottom: 4,
    '&::-webkit-scrollbar': {
      height: 3,
      marginTop: 4
    },
    '&::-webkit-scrollbar-thumb': {
      '-webkit-border-radius': '10px',
      'border-radius': '10px',
      background: 'rgba(173, 184, 204, 1)'
    }
  },
  responsiveButton: {
    padding: '8px 12px',
    height: 33,
    width: 'fit-content',
    borderRadius: 8,
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '120%',
    whiteSpace: 'nowrap',
    transition:
      'background-color 250ms ease, color 250ms ease, border-color 250ms ease',
    '& path': {
      transition: 'stroke 250ms ease'
    },
    [theme.breakpoints.down('1429')]: {
      fontSize: '12px'
    },
    [theme.breakpoints.down('660')]: {
      fontSize: '10px'
    }
  },
  unselectedButton: {
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#BEBEBE',
    backgroundColor: '#FFFFFF',
    '&:hover': {
      borderColor: '#BEBEBE',
      backgroundColor: '#FFFFFF'
    },
    '& p': {
      color: '#7B7B7B'
    }
  },
  selectedButton: {
    backgroundColor: ' #386093',
    border: 'unset !important',
    '&:hover': {
      backgroundColor: '#386093 !important',
      border: 'unset !important'
    },
    '& p': {
      color: '#FFFFFF'
    }
  },
  optionBtnRootClass: {
    width: 'fit-content'
  },
  menuDots: {
    cursor: 'pointer',
    '& circle': {
      fill: '#386093',
      fillOpacity: 1
    }
  },
  tabNavigation: {
    marginBottom: 16
  },
  messageMenu: {
    '& .MuiPaper-elevation8': {
      boxShadow: 'none'
    },
    '& .MuiPaper-root': {
      width: 200,
      height: 'fit-content'
    },
    '& .MuiList-padding': {
      padding: '20px 24px'
    }
  },
  messageMenuAction: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 12,
    '& .action': {
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      columnGap: 8,
      '& p, span': {
        color: '#BEBEBE',
        fontWeight: 500,
        margin: 0,
        fontSize: 14,
        lineHeight: '120%'
      },
      '& span': {
        fontWeight: 700
      },
      '& svg': {
        '& path': {
          stroke: '#BEBEBE'
        }
      },
      '&:hover': {
        '& p,span': {
          color: '#386093'
        },
        '& svg': {
          '& path': {
            stroke: '#386093'
          }
        }
      }
    }
  }
}))

const SidebarMessages = props => {
  const {
    roomMenu,
    setRoomMenuType,
    roomsReducer,
    match,
    getChats,
    currentUser,
    handleChangeDestination,
    handleClickOptions,
    getRoomsRSPA,
    clearPrivateRooms,
    clearGroupRooms,
    clearMediationRooms,
    isAdmin,
    handleClickRoom,
    handleOpenExportModal
  } = props
  const hasAccess = useHasAccess()
  const classes = useStyles()
  const chatOptions = [
    { label: 'Conversas diretas', int: 1 },
    { label: 'Grupos', int: 0 },
    { label: 'Intervenções', int: 2 }
  ]
  const roomComponentDefaultParams = {
    roomMenu,
    currentUser,
    getChats,
    match,
    handleClickRoom
  }
  const [selectedOption, setSelectedOption] = React.useState(chatOptions[0])
  const menuDotsAnchorRef = React.useRef(null)
  const [openMenuDots, setOpenMenuDots] = React.useState({
    open: false,
    moderationCount: '00'
  })
  const archiveRoomsFeatureFlag = featureFlags.archiveRoomsFeature

  const handleSelectOption = opt => {
    if (selectedOption.int !== opt.int) {
      setSelectedOption(opt)
      setRoomMenuType(+opt.int)
    }

    const privateTypeWithoutRooms =
      +opt.int === 1 && !(roomsReducer.privateRooms?.items?.length > 0)
    const groupTypeWithoutRooms =
      +opt.int === 0 && !(roomsReducer.groupRooms.items.length > 0)
    const mediationTypeWithoutRooms =
      +opt.int === 2 && !(roomsReducer.mediationRooms?.items?.length > 0)

    if (
      privateTypeWithoutRooms ||
      groupTypeWithoutRooms ||
      mediationTypeWithoutRooms
    ) {
      getChats({ page: 1 })
    }
  }

  const handleOpenMenuDots = async event => {
    setOpenMenuDots(prev => ({
      ...prev,
      open: !prev.open
    }))
  }

  const handleCloseMenuDots = () => {
    setOpenMenuDots(prev => ({ ...prev, open: false }))
  }

  const handleClickAction = action => {
    handleClickOptions(action)
    handleCloseMenuDots()
  }

  React.useEffect(() => {
    let mounted = true

    const load = async () => {
      try {
        await getChats({ page: 1 })

        const res = await getRoomsRSPA({
          params: {
            'q[messages_moderation_status_eq]': 1
          }
        })

        if (!mounted) return

        setOpenMenuDots(prev => ({
          ...prev,
          moderationCount: res.meta.total_count
        }))
      } catch (error) {
        console.error(error)
      }
    }

    load()

    return () => {
      mounted = false
    }
  }, [])

  React.useEffect(
    () => {
      if (roomMenu.current_room_type || roomMenu.current_room_type === 0) {
        setSelectedOption(
          chatOptions.find(item => item.int === roomMenu.current_room_type)
        )
      } else {
        setRoomMenuType(1)
        setSelectedOption(chatOptions.find(item => item.int === 1))
      }
    },
    [roomMenu.current_room_type]
  )

  return (
    <div className={classes.root}>
      <div className={classes.messageSideBarHeader}>
        <h1 className='label'>Mensagens</h1>
        <div
          className={`btn_options_wrapper ${
            !isAdmin ? 'without_menu_dots' : ''
          }`}
        >
          <div className='shortcut_options'>
            <Tooltip title='Nova mensagem'>
              <span>
                <StyledSVG
                  onClick={() => handleClickAction(1)}
                  src={privateRoomsSVG}
                  width={32}
                  height={32}
                />
              </span>
            </Tooltip>
          </div>
          {(isAdmin || archiveRoomsFeatureFlag) && (
            <div ref={menuDotsAnchorRef}>
              <StyledSVG
                onClick={event => handleOpenMenuDots(event)}
                className={classes.menuDots}
                src={menuDotsSVG}
                width={20}
                height={20}
              />
            </div>
          )}
          <Menu
            id='attach-menu'
            className={classes.messageMenu}
            anchorEl={menuDotsAnchorRef.current}
            keepMounted={false}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            open={openMenuDots.open}
            onClose={() => handleCloseMenuDots()}
          >
            <div className={classes.messageMenuAction}>
              {isAdmin && (
                <div onClick={() => handleClickAction(2)} className='action'>
                  <StyledSVG src={moderationRoomsSVG} width={20} height={24} />
                  <p>Moderação</p>
                  <span>({openMenuDots.moderationCount || '00'})</span>
                </div>
              )}
              {archiveRoomsFeatureFlag && (
                <div onClick={() => handleClickAction(3)} className='action'>
                  <StyledSVG src={archivedRoomsSVG} width={20} height={20} />
                  <p>Arquivadas</p>
                </div>
              )}
              {isAdmin && (
                <div onClick={() => handleOpenExportModal()} className='action'>
                  <StyledSVG src={exportSVG} width={20} height={20} />
                  <p>Exportar mensagens</p>
                </div>
              )}
            </div>
          </Menu>
        </div>
      </div>
      {hasAccess(accessSchoolChat) && (
        <TabNavigation
          baseClass={classes.tabNavigation}
          defaultOption={roomMenu.current_destination}
          options={[
            {
              id: 1,
              key: 'Para você',
              handleChange: () => {
                handleChangeDestination(1)
              }
            },
            {
              id: 0,
              key: 'Escola',
              handleChange: () => {
                handleChangeDestination(0)
              }
            }
          ]}
        />
      )}
      <div className={classes.chatOptionsWrapper}>
        {[
          chatOptions
            .filter(item => {
              if (item.int === 2) {
                return (
                  hasAccess(accessMediations) &&
                  roomMenu.current_destination === 1
                )
              }
              return true
            })
            .map(option => {
              const isSelected = selectedOption.int === option.int
              return (
                <Button
                  key={option.int}
                  rootClass={classes.optionBtnRootClass}
                  onClick={() => {
                    handleSelectOption(option)
                  }}
                  {...(isSelected ? { variant: 'outlined' } : {})}
                  className={`${classes.responsiveButton} ${
                    isSelected
                      ? classes.selectedButton
                      : classes.unselectedButton
                  }`}
                >
                  <p>{option.label}</p>
                </Button>
              )
            })
        ]}
      </div>
      <div className={classes.mainContent}>
        {selectedOption.int === 1 && (
          <PrivateRoomMenuMessages
            privateRooms={roomsReducer.privateRooms}
            clearPrivateRooms={clearPrivateRooms}
            {...roomComponentDefaultParams}
          />
        )}
        {selectedOption.int === 0 && (
          <GroupRoomMenuMessages
            groupRooms={roomsReducer.groupRooms}
            clearGroupRooms={clearGroupRooms}
            {...roomComponentDefaultParams}
          />
        )}
        {selectedOption.int === 2 && (
          <MediationRoomMenuMessages
            mediationRooms={roomsReducer.mediationRooms}
            clearMediationRooms={clearMediationRooms}
            {...roomComponentDefaultParams}
          />
        )}
      </div>
    </div>
  )
}

export default SidebarMessages
